import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useAuth } from '../services/authProvider'
import { DAILY_LOG_STATUS, fmtDateTH, fmtDateTimeTH } from './aoShared'

const emptyForm = {
  dl_content: '', dl_teacher_present: '', dl_teacher_absent: '',
  dl_student_present: '', dl_student_absent: '', dl_remark: '',
}

// จดหมายเหตุรายวัน — ธุรการ (23) ช่วยบันทึก, ผอ. (90) บันทึก/ตรวจ/ลงนาม
export default function DailyLog() {
  const { posIds } = useAuth()
  const canSign = (posIds || []).some(p => [90, 99].includes(p))

  const today = new Date().toLocaleDateString('en-CA')
  const [date, setDate] = useState(today)
  const [log, setLog] = useState(null) // รายการของวันที่เลือก (null = ยังไม่บันทึก)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  // รายการเดือนล่าสุด (ตารางด้านล่าง)
  const [month, setMonth] = useState(today.slice(0, 7)) // YYYY-MM
  const [logs, setLogs] = useState([])

  const fetchDay = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/ao/daily-logs/${date}`)
      if (res.data.success) {
        const row = res.data.data
        setLog(row)
        setForm(row ? {
          dl_content: row.dl_content || '',
          dl_teacher_present: row.dl_teacher_present ?? '',
          dl_teacher_absent: row.dl_teacher_absent ?? '',
          dl_student_present: row.dl_student_present ?? '',
          dl_student_absent: row.dl_student_absent ?? '',
          dl_remark: row.dl_remark || '',
        } : emptyForm)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [date])

  const fetchMonth = useCallback(async () => {
    try {
      const [y, m] = month.split('-')
      const res = await axiosInstance.get(`/api/ao/daily-logs?year=${y}&month=${Number(m)}`)
      if (res.data.success) setLogs(res.data.data)
    } catch { /* ignore */ }
  }, [month])

  useEffect(() => { fetchDay() }, [fetchDay])
  useEffect(() => { fetchMonth() }, [fetchMonth])

  const signed = log?.dl_status === 'signed'

  const handlePrefill = async () => {
    try {
      const res = await axiosInstance.get(`/api/ao/daily-logs/prefill/${date}`)
      if (res.data.success) {
        const p = res.data.data
        setForm(f => ({
          ...f,
          dl_teacher_present: p.teacher_present,
          dl_teacher_absent: p.teacher_absent,
          dl_student_present: p.student_present,
          dl_student_absent: p.student_absent,
        }))
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ดึงสถิติไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await axiosInstance.post('/api/ao/daily-logs', { dl_date: date, ...form })
      if (res.data.success) {
        setLog(res.data.data)
        fetchMonth()
        Swal.fire({ icon: 'success', title: 'บันทึกเรียบร้อย', timer: 1200, showConfirmButton: false })
      }
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!log) return
    const result = await Swal.fire({
      icon: 'question',
      title: 'เสนอผู้อำนวยการลงนาม?',
      text: `จดหมายเหตุวันที่ ${fmtDateTH(date)}`,
      showCancelButton: true,
      confirmButtonText: 'เสนอ ผอ.',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/daily-logs/${log.dl_id}/submit`)
      fetchDay()
      fetchMonth()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เสนอไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const handleSign = async () => {
    if (!log) return
    const result = await Swal.fire({
      icon: 'question',
      title: 'ลงนามรับรองจดหมายเหตุ?',
      text: `วันที่ ${fmtDateTH(date)}`,
      input: 'text',
      inputPlaceholder: 'ความเห็น (ไม่บังคับ)',
      showCancelButton: true,
      confirmButtonText: 'ลงนาม',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/daily-logs/${log.dl_id}/sign`, { note: result.value || '' })
      fetchDay()
      fetchMonth()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ลงนามไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const st = log ? (DAILY_LOG_STATUS[log.dl_status] || DAILY_LOG_STATUS.draft) : null

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-booklet-line me-2 text-primary" />
              จดหมายเหตุรายวัน
            </h4>
            <Button as={Link} to={`/admin-office/daily-log/print?month=${month}`} variant="outline-secondary" size="sm">
              <i className="ri-printer-line me-1" />พิมพ์รายเดือน
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Row className="g-3">
            <Col lg={7}>
              <Card className="card-one">
                <Card.Header className="d-flex align-items-center gap-2">
                  <Form.Control size="sm" type="date" style={{ width: 170 }} max={today}
                    value={date} onChange={e => setDate(e.target.value)} />
                  {st && <Badge bg={st.bg}>{st.label}</Badge>}
                  {log?.dl_status === 'signed' && (
                    <span className="text-muted fs-sm ms-auto">
                      ลงนามโดย {log.signed_by_name} · {fmtDateTimeTH(log.dl_signed_at)}
                    </span>
                  )}
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                  ) : (
                    <>
                      <Form.Group className="mb-2">
                        <Form.Label className="fs-sm mb-1">เหตุการณ์สำคัญประจำวัน</Form.Label>
                        <Form.Control as="textarea" rows={5} size="sm" disabled={signed}
                          placeholder="บันทึกเหตุการณ์สำคัญของโรงเรียนในวันนี้..."
                          value={form.dl_content}
                          onChange={e => setForm(f => ({ ...f, dl_content: e.target.value }))} />
                      </Form.Group>

                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Form.Label className="fs-sm mb-0">สถิติมา-ขาด</Form.Label>
                        {!signed && (
                          <Button size="sm" variant="link" className="p-0 fs-sm" onClick={handlePrefill}>
                            <i className="ri-refresh-line me-1" />ดึงจากระบบเช็คชื่อ/การลา
                          </Button>
                        )}
                      </div>
                      <Row className="g-2 mb-2">
                        {[
                          ['dl_teacher_present', 'ครูมา'],
                          ['dl_teacher_absent', 'ครูลา/ขาด'],
                          ['dl_student_present', 'นักเรียนมา'],
                          ['dl_student_absent', 'นักเรียนขาด'],
                        ].map(([k, label]) => (
                          <Col xs={3} key={k}>
                            <Form.Label className="fs-sm mb-1">{label}</Form.Label>
                            <Form.Control size="sm" type="number" min="0" disabled={signed}
                              value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
                          </Col>
                        ))}
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
                        <Form.Control size="sm" as="textarea" rows={2} disabled={signed}
                          value={form.dl_remark}
                          onChange={e => setForm(f => ({ ...f, dl_remark: e.target.value }))} />
                      </Form.Group>

                      {log?.dl_sign_note && (
                        <Alert variant="light" className="border fs-sm py-2">
                          <span className="text-muted">ความเห็น ผอ.:</span> {log.dl_sign_note}
                        </Alert>
                      )}

                      {!signed && (
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="primary" disabled={saving} onClick={handleSave}>
                            {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
                            บันทึก
                          </Button>
                          {log && log.dl_status === 'draft' && (
                            <Button size="sm" variant="outline-primary" onClick={handleSubmit}>
                              <i className="ri-send-plane-line me-1" />เสนอ ผอ. ลงนาม
                            </Button>
                          )}
                          {log && canSign && (
                            <Button size="sm" variant="success" className="ms-auto" onClick={handleSign}>
                              <i className="ri-quill-pen-line me-1" />ลงนามรับรอง
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="card-one">
                <Card.Header className="d-flex align-items-center gap-2">
                  <Card.Title as="h6" className="mb-0">รายการประจำเดือน</Card.Title>
                  <Form.Control size="sm" type="month" style={{ width: 160 }} className="ms-auto"
                    value={month} onChange={e => setMonth(e.target.value)} />
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: 460, overflowY: 'auto' }}>
                  {logs.length === 0 ? (
                    <div className="text-center text-muted py-4 fs-sm">ยังไม่มีบันทึกในเดือนนี้</div>
                  ) : (
                    <Table hover className="mb-0 fs-sm">
                      <tbody>
                        {logs.map(l => {
                          const ls = DAILY_LOG_STATUS[l.dl_status] || DAILY_LOG_STATUS.draft
                          return (
                            <tr key={l.dl_id} style={{ cursor: 'pointer' }} onClick={() => setDate(l.dl_date)}>
                              <td style={{ width: 110 }} className={l.dl_date === date ? 'fw-bold' : ''}>
                                {fmtDateTH(l.dl_date)}
                              </td>
                              <td className="text-truncate" style={{ maxWidth: 200 }} title={l.dl_content || ''}>
                                {l.dl_content || <span className="text-muted">—</span>}
                              </td>
                              <td style={{ width: 100 }} className="text-end">
                                <Badge bg={ls.bg}>{ls.label}</Badge>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </>
  )
}
